import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import cx from 'classnames';
import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Link as MaterialLink,
  Typography,
  Avatar,
} from '@material-ui/core';
import MemberStatusView from 'common/components/MemberStatusView/MemberStatusView';
import profilePlaceholder from 'img/photo-placeholder.png';
import Button from '../Button/Button';

const useStyles = makeStyles(theme =>
  createStyles({
    link: {
      padding: '2px 5px 2px 0',
      '& button': {
        textAlign: 'left',
        textTransform: 'none',
      },
      '& h5': {
        color: theme.palette.primary.main,
      },
      '&:hover': {
        backgroundColor: 'transparent',
        '& h5': { color: theme.palette.primary.dark },
      },
    },
    avatarWrap: {
      position: 'relative',
      flex: 'none',
      borderRadius: '50%',
      backgroundColor: theme.palette.secondary.light,
    },
    avatarInitial: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translateX(-50%) translateY(-50%)',
      fontSize: '12px',
      fontWeight: 700,
      color: theme.palette.darkBackground?.light,
      whiteSpace: 'nowrap',
    },
  }),
);

interface IProfileLinkWithAvatar {
  link: string;
  label: string;
  imageUrl?: string | null;
  memberStatus?: string;
  initials: string;
  titleClass?: string;
  avatarClass?: string;
  size?: number;
  onClick?: () => void;
}

const ProfileLinkWithAvatar: FC<IProfileLinkWithAvatar> = ({
  link,
  label,
  imageUrl,
  memberStatus,
  initials,
  titleClass,
  avatarClass,
  size = 32,
  onClick,
}) => {
  const classes = useStyles();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dynamicClasses = makeStyles(() =>
    createStyles({
      avatar: {
        width: `${size}px`,
        height: `${size}px`,
      },
    }),
  )();

  const navigate = useNavigate();

  const handleLinkClick = event => {
    if (onClick) onClick();
    navigate(link);
    event.stopPropagation();
  };

  return (
    <Button className={classes.link} onClick={handleLinkClick}>
      <MaterialLink component="button" underline="none">
        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid item>
            <Box className={cx(dynamicClasses.avatar, classes.avatarWrap, avatarClass)}>
              {initials && !imageUrl ? (
                <Box className={classes.avatarInitial}>{initials}</Box>
              ) : (
                <Avatar
                  src={imageUrl || profilePlaceholder}
                  className={cx(dynamicClasses.avatar, avatarClass)}
                  alt={label}
                />
              )}
            </Box>
          </Grid>
          {!!memberStatus && (
            <Grid item>
              <MemberStatusView status={memberStatus || ''} />
            </Grid>
          )}

          <Grid item>
            <Typography variant="h5" noWrap className={titleClass}>
              {label}
            </Typography>
          </Grid>
        </Grid>
      </MaterialLink>
    </Button>
  );
};

export default React.memo(ProfileLinkWithAvatar);
